// ---------------------------------------------------------------------------
// PWA Caisse Tablette — useHeartbeat hook
// Starts all background services (heartbeat, offline queue, reservation/table
// sync) when authenticated, ensures a cash session exists, and loads initial
// data. Stops everything on unmount or logout.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import { useAuthStore } from '@/store/auth-store';
import * as syncManager from '@/services/sync-manager';
import { apiClient } from '@/services/api-client';
import { offlineQueue } from '@/services/offline-queue';
import { reservationSync } from '@/services/reservation-sync';
import { tableSync } from '@/services/table-sync';
import { useAppStore } from '@/store/app-store';

/**
 * Mount-level hook that orchestrates all background services.
 * Should be used once in the authenticated layout / root component.
 *
 * When the user becomes authenticated:
 *   1. Starts heartbeat, offline queue, reservation sync, table sync.
 *   2. Ensures a cash session is open (NF525).
 *   3. Loads initial data (products, reservations, tables, config).
 *
 * On unmount or logout: stops all services.
 */
export function useHeartbeat(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    async function bootstrap(): Promise<void> {
      try {
        useAppStore.getState().setLoading(true);

        // Start all background sync services
        await syncManager.startAll();

        if (cancelled) return;

        // Ensure cash session for NF525 compliance
        await syncManager.ensureCashSession();

        if (cancelled) return;

        // Load initial data
        await initialDataLoad();
      } catch (error) {
        console.error('[useHeartbeat] Bootstrap error:', error);
      } finally {
        if (!cancelled) {
          useAppStore.getState().setLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
      syncManager.stopAll();
    };
  }, [isAuthenticated]);
}

// ---------------------------------------------------------------------------
// Initial data load (parallel)
// ---------------------------------------------------------------------------

async function initialDataLoad(): Promise<void> {
  console.log('[useHeartbeat] Loading initial data...');

  try {
    const syncResult = await apiClient.syncData();

    if (syncResult.success && syncResult.data.products) {
      // Cache products for offline fallback
      const products =
        syncResult.data.products.products ?? [];
      if (Array.isArray(products) && products.length > 0) {
        await offlineQueue.cacheProducts(products);
      }
    }

    // Load floor plans (only on first boot, auto-sync handles the rest)
    await tableSync.loadFloorPlans();
    await tableSync.syncTables();

    // Sync reservations from server
    await reservationSync.sync();

    console.log('[useHeartbeat] Initial data loaded');
  } catch (error) {
    console.warn(
      '[useHeartbeat] Initial data load partial failure:',
      (error as Error).message,
    );
  }
}
